import * as React from "react";
import {useCallback, useEffect, useMemo} from "react";
import {useDispatch, useSelector} from "react-redux";
import {ProductSummary} from "../../model/product/ProductSummary";
import {Map} from "immutable";
import {IManagerStrategiesState} from "./ManagerStrategiesReducer";
import managerStrategiesActions from "./ManagerStrategiesActions";
import {IApplicationRootState} from "../../../applicationState";
import {scrollToElement} from "../../utils/browserUtil";
import {byRatingAndName, fixBlankAndTranslate, mapProductForDisplay} from "../../utils/researchUtil";
import {AnyThunkDispatch} from "../../../commonTypes";
import {IResearchFilterConfig, useResearchFilters} from "../research/useResearchFilters";
import {useStickyControls} from "../../utils/useStickyControls";
import {ResearchFilterBar} from "../research/ResearchFilterBar.component";
import {RESEARCH_TABLE_COLUMNS, ResearchTable} from "../research/ResearchTable.component";
import {ResearchPagination} from "../research/ResearchPagination.component";
import {DisplayProductSummary, FacetedFilters, IResearchPagination} from "../../model/ResearchPageState.model";
import {getTranslation} from "../../utils/translationUtil";

export interface IManagerStrategiesProps {
    managerId: number;
    productSummaries: Map<number, ProductSummary>;
}

const MANAGER_FILTER_FIELDS = ["assetType", "productRating", "approach", "category", "region", "style"] as const;

const MANAGER_SEARCH_FIELDS: (keyof DisplayProductSummary)[] = [
    "name", "assetType", "productRating", "approach", "category", "region", "style",
];

const EXCLUDED_COLUMNS = new Set(["managerName", "oddRatingCurrent"]);

export const ManagerStrategies: React.FunctionComponent<IManagerStrategiesProps> = (props) => {
    const {managerId, productSummaries} = props;
    const dispatch: AnyThunkDispatch = useDispatch();

    const strategiesStateFromStore: IManagerStrategiesState | undefined
        = useSelector((state: IApplicationRootState) => state.managerStrategiesState);

    const allData: DisplayProductSummary[] = useMemo(() => {
        return productSummaries
            .filter((it: ProductSummary) => it ? it.manager.managerBackstopId === managerId : false)
            .valueSeq()
            .toArray()
            .map((it: ProductSummary) => fixBlankAndTranslate(it))
            .sort(byRatingAndName)
            .map(mapProductForDisplay);
    }, [managerId, productSummaries]);

    const filterConfig = useMemo((): IResearchFilterConfig => ({
        stateSelector: (s: IApplicationRootState) =>
            s.managerStrategiesState?.managerId === managerId ? s.managerStrategiesState : undefined,
        actions: {
            updateFacetedFiltersAction: (facetedFilters: FacetedFilters) =>
                managerStrategiesActions.updateFacetedFiltersAction(managerId, facetedFilters),
            updateSearchTextAction: (searchText: string) =>
                managerStrategiesActions.updateSearchTextAction(managerId, searchText),
            updatePaginationAction: (pagination: IResearchPagination) =>
                managerStrategiesActions.updatePaginationAction(managerId, pagination),
            clearAllFiltersAction: () =>
                managerStrategiesActions.clearAllFiltersAction(managerId),
        },
        filterFields: MANAGER_FILTER_FIELDS,
        searchFields: MANAGER_SEARCH_FIELDS,
    }), [managerId]);

    const filters = useResearchFilters(allData, filterConfig);

    const {containerRef, stickyControlsRef} = useStickyControls(
        [filters.facetedFilters, filters.searchText],
    );

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [filters.pagination.pageIndex]);

    useEffect(() => {
        if (strategiesStateFromStore?.managerId && strategiesStateFromStore.managerId !== managerId) {
            dispatch(managerStrategiesActions.clearStrategiesAction());
        }
    }, [managerId]);

    useEffect(() => {
        if (strategiesStateFromStore?.managerId && strategiesStateFromStore.managerId === managerId) {
            if (strategiesStateFromStore.scrollToBackstopId) {
                setTimeout(() => {
                    scrollToElement(`#product_${strategiesStateFromStore.scrollToBackstopId}`, 100, true);
                }, 50);
            }
        }
    }, []);

    const handleNavigateToProduct = useCallback((backstopId: number) => {
        dispatch(managerStrategiesActions.setScrollToBackstopAction(managerId, backstopId));
    }, [dispatch, managerId]);

    const managerColumns = useMemo(() => {
        return RESEARCH_TABLE_COLUMNS.filter((col) => !EXCLUDED_COLUMNS.has(col.key));
    }, []);

    const handleExportCsv = useCallback(() => {
        const exportData = filters.filteredData.map((row) => ({
            name: row.name,
            assetType: row.assetType,
            productRating: row.productRating,
            approach: row.approach,
            category: row.category,
            region: row.region,
            style: row.style,
        }));
        const headers = [
            getTranslation("research-page.strategy-name", "Strategy Name"),
            getTranslation("research-page.asset-class", "Asset Class"),
            getTranslation("research-page.rating", "Rating"),
            getTranslation("research-page.approach", "Approach"),
            getTranslation("research-page.category", "Category"),
            getTranslation("research-page.region", "Region"),
            getTranslation("research-page.style", "Style"),
        ];
        const rows = exportData.map((row) =>
            [row.name, row.assetType, row.productRating, row.approach, row.category, row.region, row.style]
                .map((val) => `"${String(val || "").replace(/"/g, '""')}"`).join(","));
        const csv = [headers.join(","), ...rows].join("\n");
        const blob = new Blob([csv], {type: "text/csv;charset=utf-8;"});
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "manager_strategies.csv";
        link.click();
        URL.revokeObjectURL(url);
    }, [filters.filteredData]);

    return (
        <div className="strategies-container" data-testid="strategies-container" ref={containerRef}>
            <div className="strategies-container__sticky-controls" ref={stickyControlsRef}>
                <ResearchFilterBar
                    filters={filters}
                    filteredData={filters.filteredData}
                    onExportCsv={handleExportCsv}
                />
            </div>
            <ResearchTable
                columns={managerColumns}
                data={filters.paginatedData}
                searchText={filters.searchText}
                totalFiltered={filters.totalFiltered}
                onNavigateToProduct={handleNavigateToProduct}
                caption={getTranslation("manager.strategies-table-caption", "Manager strategies table")}
            />
            <ResearchPagination
                pagination={filters.pagination}
                totalItems={filters.totalFiltered}
                onPaginationChange={filters.setPagination}
            />
        </div>
    );
};
